import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  cancelRedemptionRequest,
  enrichProjectsWithHoldingsProjectFields,
  fetchPlatformSettings,
  fetchPortfolio,
  fetchRedemptionBalance,
  fetchRedemptionHistory,
  listProjects,
  unwrapListPayload,
  unwrapRedemptionHistoryPayload,
} from "../services/investmentService";
import IncomingTransfersAcceptCard from "../components/investor/IncomingTransfersAcceptCard";
import InvestorHistoryFilters from "../components/investor/InvestorHistoryFilters";
import InvestorPageHeader from "../components/investor/InvestorPageHeader";
import InvestorSectionHeading from "../components/investor/InvestorSectionHeading";
import SharesManagementMarketTabs from "../components/investor/SharesManagementMarketTabs";
import { REDEEMABLE_PROJECTS_LABEL } from "../utils/investorNavConfig";
import { isInvestorRedeemEligible, isInvestorTransferEligible } from "../utils/investorRedeemEligibility";
import {
  buildLifecycleBuckets,
  filterInvestorCatalogProjects,
  isCompletedProjectStatus,
  projectTitleWithCode,
} from "../utils/projectViews";
import { redemptionStatusLabel, redemptionStatusTone } from "../utils/redemptionLabels";
import InvestorMetricGrid from "../components/investor/InvestorMetricGrid";
import InvestorConfirmDialog from "../components/investor/InvestorConfirmDialog";
import RedeemSharesRequestModal from "../components/investor/RedeemSharesRequestModal";
import ShareBalanceBar from "../components/investor/ShareBalanceBar";
import RedemptionOfferDetailModal from "../components/investor/RedemptionOfferDetailModal";

const shareFmt = new Intl.NumberFormat("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatShare = (n) => shareFmt.format(Number(n || 0));

const DEFAULT_FILTERS = {
  project_id: "",
  status: "",
  date_from: "",
  date_to: "",
  q: "",
  sort: "newest",
  page: 1,
};

export default function RedemptionHistoryPage() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [count, setCount] = useState(0);
  const [balances, setBalances] = useState([]);
  const [holdingsByProject, setHoldingsByProject] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [detailRow, setDetailRow] = useState(null);
  const [cancellingId, setCancellingId] = useState("");
  const [redeemModalBalance, setRedeemModalBalance] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [cancelConfirmRow, setCancelConfirmRow] = useState(null);
  const [marketTabCounts, setMarketTabCounts] = useState({
    fundraising: 0,
    funded: 0,
    transferable: 0,
    redeemPending: 0,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      const params = { page: filters.page, page_size: 25, sort: filters.sort };
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.q) params.q = filters.q;
      const [{ data: hist }, { data: bal }] = await Promise.all([
        fetchRedemptionHistory(params),
        fetchRedemptionBalance(),
      ]);
      const unpacked = unwrapRedemptionHistoryPayload(hist);
      setRows(unpacked.rows);
      setSummary(unpacked.summary);
      setCount(unpacked.count);
      setMarketTabCounts((prev) => ({
        ...prev,
        redeemPending: Number(unpacked.summary?.pending_count ?? unpacked.count ?? 0),
      }));
      const balanceRows = Array.isArray(bal?.balances) ? bal.balances : bal?.project_id ? [bal] : [];
      setBalances(balanceRows);
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Failed to load redemption history.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listProjects(), fetchPlatformSettings(), fetchPortfolio()])
      .then(([projectsRes, settingsRes, portfolioRes]) => {
        if (cancelled) return;
        const holdings = unwrapListPayload(portfolioRes.data);
        const projects = filterInvestorCatalogProjects(
          enrichProjectsWithHoldingsProjectFields(unwrapListPayload(projectsRes.data), holdings)
        );
        const buckets = buildLifecycleBuckets(projects, settingsRes.data);
        const map = new Map();
        for (const h of holdings) {
          const id = h.project?.id;
          if (id == null || id === "") continue;
          map.set(String(id), { holding: h, project: h.project });
        }
        for (const p of projects) {
          const id = String(p.id);
          if (map.has(id)) {
            map.get(id).project = p;
          }
        }
        setHoldingsByProject(map);
        const transferable = holdings.filter((h) => isInvestorTransferEligible(h, h.project)).length;
        setMarketTabCounts((prev) => ({
          ...prev,
          fundraising: buckets.fundraising.length,
          funded: buckets.funded.filter((p) => !isCompletedProjectStatus(p.status)).length,
          transferable,
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const projectOptions = useMemo(() => {
    const map = new Map();
    for (const b of balances) {
      const id = b.project_id;
      if (!id) continue;
      const label =
        projectTitleWithCode({ project_code: b.project_code, name: b.project_name }) || id;
      map.set(String(id), label);
    }
    for (const r of rows) {
      if (r.project && r.project_display) map.set(String(r.project), r.project_display);
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [balances, rows]);

  const redeemEligibleBalances = useMemo(
    () =>
      balances.filter((b) => {
        const avail = Number(b.shares_available_for_redemption || 0);
        if (avail <= 0) return false;
        const entry = holdingsByProject.get(String(b.project_id));
        if (!entry) return false;
        return isInvestorRedeemEligible(entry.holding, entry.project);
      }),
    [balances, holdingsByProject]
  );

  const totals = useMemo(() => {
    let locked = 0;
    let available = 0;
    let redeemed = 0;
    for (const b of balances) {
      locked += Number(b.shares_pending_redemption || 0);
      available += Number(b.shares_available_for_redemption || 0);
      redeemed += Number(b.shares_redeemed_total || 0);
    }
    return { locked, available, redeemed };
  }, [balances]);

  const executeCancelRedemption = async (row) => {
    if (!row?.id || cancellingId) return;
    setCancellingId(row.id);
    setMessage("");
    try {
      await cancelRedemptionRequest(row.id);
      setCancelConfirmRow(null);
      setSuccessMsg(
        row.redemption_ref
          ? `Redemption ${row.redemption_ref} cancelled. Locked shares have been released.`
          : "Redemption request cancelled."
      );
      await load();
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Could not cancel request.");
    } finally {
      setCancellingId("");
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / 25));

  return (
    <div className="page investor-panel investor-panel-enhanced">
      <p className="wallet-breadcrumb investor-module-breadcrumb">
        <Link to="/investments">Shares Management</Link>
        <span aria-hidden="true"> / </span>
        <span>{REDEEMABLE_PROJECTS_LABEL}</span>
      </p>
      <InvestorPageHeader
        icon="redeemable"
        title={REDEEMABLE_PROJECTS_LABEL}
        subtitle="Funded projects where you can submit redemption requests. Track pending requests, locked shares, and settlement."
      />
      <div className="invx-filters card" style={{ marginBottom: 16 }}>
        <SharesManagementMarketTabs
          activeView="fundraising"
          onViewChange={() => {}}
          counts={marketTabCounts}
        />
      </div>
      <IncomingTransfersAcceptCard
        onAccepted={() => {
          load();
          setSuccessMsg("Transfer accepted. Your holdings and wallet are updated.");
        }}
        onError={setMessage}
      />
      {successMsg ? <p className="msg msg-success">{successMsg}</p> : null}
      {message ? <p className="msg msg-error">{message}</p> : null}
      <InvestorMetricGrid
        metrics={[
          { key: "p", label: "Pending requests", value: String(summary?.pending_count ?? 0) },
          { key: "l", label: "Shares locked", value: formatShare(totals.locked) },
          { key: "a", label: "Available to redeem", value: formatShare(totals.available), highlight: true },
          { key: "r", label: "Lifetime redeemed", value: formatShare(summary?.fulfilled_shares ?? totals.redeemed) },
        ]}
      />
      <section className="investor-section">
        <InvestorSectionHeading icon="redeem-eligible">Redeem-eligible projects</InvestorSectionHeading>
        <p className="investor-section__desc muted" style={{ marginTop: 0 }}>
          Same rules as redeeming from a funded project: lock-in ended, project eligible, and each request is capped by
          Super Admin <strong>max redemption per request (% of holding)</strong>. Use <strong>Redeem Shares</strong> to
          submit.
        </p>
        {loading && redeemEligibleBalances.length === 0 ? (
          <p className="muted">Loading…</p>
        ) : redeemEligibleBalances.length === 0 ? (
          <p className="muted">No shares available to redeem right now. Check lock-in rules or pending requests above.</p>
        ) : (
          <ul className="redeem-eligible-list">
            {redeemEligibleBalances.map((b) => {
              const label =
                projectTitleWithCode({ project_code: b.project_code, name: b.project_name }) || "Project";
              const held =
                Number(b.shares_held || 0) ||
                Number(b.shares_available_for_redemption || 0) + Number(b.shares_pending_redemption || 0);
              const available = Number(b.shares_available_for_redemption || 0);
              return (
                <li key={b.project_id || label} className="redeem-eligible-list__item">
                  <div className="redeem-eligible-list__head">
                    <strong className="redeem-eligible-list__title">{label}</strong>
                    <button
                      type="button"
                      className="btn-secondary compact-btn redeem-eligible-list__btn"
                      onClick={() => setRedeemModalBalance(b)}
                    >
                      Redeem Shares
                    </button>
                  </div>
                  <ShareBalanceBar
                    held={held}
                    locked={b.shares_pending_redemption}
                    available={available}
                    redeemed={b.shares_redeemed_total}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <InvestorHistoryFilters
        idPrefix="redemption"
        variant="redemption"
        filters={filters}
        onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
        projectOptions={projectOptions}
        onApply={load}
        onReset={() => setFilters(DEFAULT_FILTERS)}
        loading={loading}
      />

      <section className="investor-section">
        <div className="investor-section__head">
          <div>
            <InvestorSectionHeading icon="request-history">Request history</InvestorSectionHeading>
            <p className="investor-section__desc">
              Every redemption you submitted. Open <strong>Details</strong> for the full breakdown. Cancel while
              pending review.
            </p>
          </div>
        </div>
        <div className="investor-history-toolbar">
          <span className="investor-history-toolbar__count">
            {loading ? "Loading…" : `${count} record${count === 1 ? "" : "s"}`}
            {summary?.pending_count > 0 ? ` · ${summary.pending_count} pending` : ""}
          </span>
        </div>
        {Number(summary?.pending_count ?? 0) > 0 && filters.status !== "PENDING" ? (
          <div className="investor-pending-banner" role="status">
            <span>
              {summary.pending_count} pending request{summary.pending_count === 1 ? "" : "s"} not shown in this
              filter.
            </span>
            <button
              type="button"
              className="btn-secondary compact-btn"
              onClick={() => setFilters((f) => ({ ...f, status: "PENDING", page: 1 }))}
            >
              Show pending only
            </button>
          </div>
        ) : null}
        <div className="table-wrap table-wrap--cards">
          <table className="ops-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Project</th>
                <th>Redemption ID</th>
                <th>Requested</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="muted">
                    {loading ? "Loading…" : "No redemption records match your filters."}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={row.id}>
                    <td data-label="#">{(filters.page - 1) * 25 + idx + 1}</td>
                    <td data-label="Project">{row.project_display || "—"}</td>
                    <td data-label="ID">{row.redemption_ref || "—"}</td>
                    <td data-label="Requested">{formatShare(row.shares)}</td>
                    <td data-label="Status">
                      <span className={`badge ${redemptionStatusTone(row.status)}`}>{redemptionStatusLabel(row.status)}</span>
                    </td>
                    <td data-label="Actions">
                      <span className="investor-history-actions">
                        <button type="button" className="btn-secondary compact-btn" onClick={() => setDetailRow(row)}>
                          Details
                        </button>
                        {String(row.status).toUpperCase() === "PENDING" ? (
                          <button
                            type="button"
                            className="btn-danger compact-btn"
                            disabled={cancellingId === row.id}
                            onClick={() => setCancelConfirmRow(row)}
                          >
                            Cancel
                          </button>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 ? (
          <div className="investor-history-pagination">
            <button type="button" className="btn-secondary compact-btn" disabled={filters.page <= 1} onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}>
              Previous
            </button>
            <span className="muted">
              Page {filters.page} of {totalPages}
            </span>
            <button type="button" className="btn-secondary compact-btn" disabled={filters.page >= totalPages} onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}>
              Next
            </button>
          </div>
        ) : null}
      </section>

      <InvestorConfirmDialog
        open={Boolean(cancelConfirmRow)}
        title="Cancel redemption?"
        message={
          cancelConfirmRow ? (
            <>
              Cancel redemption <strong>{cancelConfirmRow.redemption_ref || "this request"}</strong>? Locked shares
              will be released.
            </>
          ) : (
            ""
          )
        }
        confirmLabel="Confirm cancel"
        cancelLabel="Keep request"
        variant="danger"
        busy={Boolean(cancelConfirmRow && cancellingId === cancelConfirmRow.id)}
        onCancel={() => !cancellingId && setCancelConfirmRow(null)}
        onConfirm={() => cancelConfirmRow && executeCancelRedemption(cancelConfirmRow)}
      />

      {redeemModalBalance ? (
        <RedeemSharesRequestModal
          balance={redeemModalBalance}
          onClose={() => setRedeemModalBalance(null)}
          onSuccess={({ redemption_ref }) => {
            setSuccessMsg(
              redemption_ref
                ? `Redemption ${redemption_ref} submitted. It will appear in request history when approved.`
                : "Redemption request submitted."
            );
            setRedeemModalBalance(null);
            load();
          }}
        />
      ) : null}

      <RedemptionOfferDetailModal
        row={detailRow}
        onClose={() => setDetailRow(null)}
        onRequestCancel={(row) => {
          setDetailRow(null);
          setCancelConfirmRow(row);
        }}
        cancelling={Boolean(detailRow && cancellingId === detailRow.id)}
      />
    </div>
  );
}